import { DateRange } from '@daypicker/react';
import { PButton, PButtonPure } from '@porsche-design-system/components-react';
import { Column, FilterFn, RowData } from '@tanstack/react-table';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useEffect, useState } from 'react';

import { ActionGroup } from '../../ActionGroup';
import { DayPicker } from '../../DayPicker';
import { Popover } from '../../Popover';
import type { FilterTableFeatures } from '../table-features';

dayjs.extend(isBetween);
export const dateBetweenFilterFn: FilterFn<any, any> = (
  row,
  columnId,
  value,
) => {
  const from = dayjs(value.from).startOf('day').toDate();
  const to = dayjs(value.to).endOf('day').toDate();

  return dayjs(row.getValue(columnId)).isBetween(from, to, null, '[]');
};

dateBetweenFilterFn.autoRemove = (val: string) => !val;

const getInitialValue = () => ({
  from: undefined,
  to: undefined,
});

export const DateRangeFilter = <
  TFeatures extends FilterTableFeatures,
  T extends RowData,
>({
  column,
  i18n,
}: {
  column: Column<TFeatures, T, unknown>;
  i18n: {
    buttonReset: string;
    buttonFilter: string;
  };
}) => {
  const filterColumn = column as unknown as Column<
    FilterTableFeatures,
    T,
    unknown
  >;
  const filterValue = filterColumn.getFilterValue() as DateRange;
  const [isVisible, setVisibility] = useState(false);

  const [range, setRange] = useState<DateRange | undefined>(getInitialValue());

  useEffect(() => {
    setRange(filterValue);
  }, [filterValue]);

  return (
    <Popover
      target={
        <PButtonPure
          type="button"
          active={!!filterValue}
          icon="filter"
          size="sm"
          onClick={() => setVisibility((value) => !value)}
          hideLabel
        />
      }
      open={isVisible}
      onChange={setVisibility}
      placement="right-start"
      withPortal
    >
      <DayPicker selected={range} mode="range" onSelect={setRange} />
      <ActionGroup>
        <PButton
          type="button"
          variant="secondary"
          icon="close"
          onClick={() => {
            setRange(getInitialValue());
            filterColumn.setFilterValue(undefined); // Set undefined to remove the filter entirely
            setVisibility(false);
          }}
        >
          {i18n.buttonReset}
        </PButton>

        <PButton
          type="button"
          onClick={() => {
            filterColumn.setFilterValue(range || undefined); // Set undefined to remove the filter entirely
            setVisibility(false);
          }}
        >
          {i18n.buttonFilter}
        </PButton>
      </ActionGroup>
    </Popover>
  );
};
